import {Mapper} from '@s-ui/domain'

/**
 * @implements Mapper
 * Use the raw response from the http repository to create a VehicleInfo Value Object.
 */
export class FromAdDetailsRawDataToVehicleInfoValueObjectMapper extends Mapper {
  constructor({
    config,
    descriptionItemValueObjectFactory,
    fromElectricFeaturesRawDataToElectricFeaturesVOMapper,
    equipmentSectionValueObjectFactory,
    equipmentValueObjectFactory,
    getUrlService,
    vehicleInfoValueObjectFactory,
    versionSpecificationsValueObjectFactory
  }) {
    super()

    const {FUEL_TYPES} = config.get('advertising')

    this._config = config
    const {bodyTypes = [], colors = []} = this._config.get('search')
    this._appName = this._config.get('APP_NAME')
    this._appNameCoches = config.get('APP_NAME_COCHES')
    this._bodyTypes = bodyTypes
    this._bodyTypesTranslations = this._config.get('tracking').BODY_TYPES_TRANSLATIONS
    this._colors = colors
    this._culture = this._config.get('DEFAULT_CULTURE')
    this._descriptionItemValueObjectFactory = descriptionItemValueObjectFactory
    this._equipmentSectionValueObjectFactory = equipmentSectionValueObjectFactory
    this._equipmentValueObjectFactory = equipmentValueObjectFactory
    this._fuelTypes = FUEL_TYPES
    this._getUrlService = getUrlService
    this._fromElectricFeaturesRawDataToElectricFeaturesVOMapper = fromElectricFeaturesRawDataToElectricFeaturesVOMapper
    this._vehicleInfoValueObjectFactory = vehicleInfoValueObjectFactory
    this._versionSpecificationsValueObjectFactory = versionSpecificationsValueObjectFactory
  }

  _getColorName = colorId => {
    const colorName = this._colors.find(color => String(color.id) === String(colorId))
    return colorName && colorName.label
  }

  _getBodyType = bodyTypeId => {
    const bodyType = this._bodyTypes.find(({id}) => id === bodyTypeId)
    return bodyType ? {bodyType: bodyType.label, bodyTypeId} : {}
  }

  _getFuelType = ({fuelTypeId}) => this._fuelTypes[fuelTypeId]?.[this._culture] || ''

  _getEquipmentDescriptions = ({equipment = []}) =>
    equipment.map(description =>
      this._descriptionItemValueObjectFactory({
        value: description
      })
    )

  _getEquipment = ({vehicleEquipment}) => {
    const hasEquipment = Boolean(vehicleEquipment.length)
    return hasEquipment
      ? vehicleEquipment.map(({description, equipment, items, title}) =>
          this._equipmentSectionValueObjectFactory({
            descriptions: this._getEquipmentDescriptions({
              equipment: equipment || items
            }),
            title: title || description
          })
        )
      : []
  }

  // Prefers `entries` (they carry the price); falls back to `items` so no extra disappears. 0 stays 0, anything else non-numeric is null.
  _getOptionalEquipmentDescriptions = ({entries, items = []}) => {
    const hasEntries = Array.isArray(entries) && entries.length > 0
    const rawEntries = hasEntries ? entries : items.map(description => ({description}))

    return rawEntries.map(({description, price}) =>
      this._descriptionItemValueObjectFactory({
        value: description,
        price: typeof price === 'number' ? price : null
      })
    )
  }

  _getOptionalEquipment = ({optionalEquipment}) =>
    optionalEquipment.map(({description, entries, equipment, items, title}) =>
      this._equipmentSectionValueObjectFactory({
        descriptions: this._getOptionalEquipmentDescriptions({entries, items: equipment || items}),
        title: title || description
      })
    )

  map = async rawData => {
    const {ad} = rawData

    const {vehicle = {}} = ad
    const {specs: adVehicleSpecs = {}} = vehicle
    const {vehicleSpecs = {}} = rawData

    const {vehicleStandardEquipmentGroup = []} = vehicleSpecs
    const {electricFeatures} = vehicleSpecs
    const {optionalEquipment = []} = adVehicleSpecs

    const stdEquipment = this._getEquipment({
      vehicleEquipment: vehicleStandardEquipmentGroup
    })
    const optEquipment = this._getOptionalEquipment({optionalEquipment})

    const mappedElectricFeatures = this._fromElectricFeaturesRawDataToElectricFeaturesVOMapper
      .setParams({summarized: true})
      .map({
        electricFeatures
      })

    const technicalDetailsUrl =
      this._appName === this._appNameCoches
        ? await this._getUrlService
            .execute({
              pageName: 'technical-details-vehicle',
              params: {
                make: adVehicleSpecs.make,
                model: adVehicleSpecs.model,
                bodyType:
                  Boolean(adVehicleSpecs.bodyTypeId) &&
                  this._bodyTypesTranslations[adVehicleSpecs.bodyTypeId]?.[this._culture],
                doors: adVehicleSpecs.doors,
                version: adVehicleSpecs.version,
                powerHp: adVehicleSpecs.power,
                fuelType:
                  Boolean(adVehicleSpecs.fuelTypeId) &&
                  this._getFuelType({
                    fuelTypeId: adVehicleSpecs.fuelTypeId
                  }),
                versionId: adVehicleSpecs.versionId,
                uniqueVehicleId: adVehicleSpecs.uniqueVehicleId
              }
            })
            .then(technicalDetailsUrlVO => technicalDetailsUrlVO.getUrl())
            .catch(() => null)
        : null

    return this._vehicleInfoValueObjectFactory({
      vehicleInfo: {
        equipment: this._equipmentValueObjectFactory({
          standard: stdEquipment,
          optional: optEquipment
        }),
        makeId: adVehicleSpecs.makeId,
        specifications: this._versionSpecificationsValueObjectFactory({
          ...this._getBodyType(adVehicleSpecs.bodyTypeId),
          acceleration: vehicleSpecs.acceleration,
          consumptionAverage: vehicleSpecs.consumption?.mixed,
          consumptionExtraUrban: vehicleSpecs.consumption?.extraUrban,
          consumptionUrban: vehicleSpecs.consumption?.urban,
          cubicCapacity: adVehicleSpecs.cubicCapacity,
          doors: adVehicleSpecs.doors,
          emissions: adVehicleSpecs.co2Emissions,
          electricFeatures: mappedElectricFeatures,
          fuelTankCapacity: vehicleSpecs.tankCapacityInLiters,
          height: vehicleSpecs.dimensionsInMillimeters?.height,
          hp: adVehicleSpecs.power,
          length: vehicleSpecs.dimensionsInMillimeters?.length,
          luggageCapacity: vehicleSpecs.trunkCapacityInLiters,
          make: adVehicleSpecs.make,
          manufacturerPrice: vehicleSpecs.manufacturerPrice,
          model: adVehicleSpecs.model,
          powerElectric: adVehicleSpecs.powerElectric,
          seats: adVehicleSpecs.seatingCapacity,
          topSpeed: vehicleSpecs.maxSpeed,
          version: adVehicleSpecs.version,
          weight: vehicleSpecs.weight,
          width: vehicleSpecs.dimensionsInMillimeters?.width
        }),
        technicalDetailsUrl,
        vehicleId: vehicleSpecs.vehicleId,
        uniqueVehicleId: adVehicleSpecs.uniqueVehicleId,
        year: vehicle.year
      }
    })
  }
}
